import { DEFAULT_SEMESTER_ID } from '@/app/lib/semesters';
import { createExploreCatalogSummaries } from '@/app/services/courseCatalogShared';
import type { ExploreCatalogSummary, RutgersCourse } from '@/app/types/interfaces';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isExploreSummary(value: unknown): value is ExploreCatalogSummary {
  if (!isRecord(value) || !isRecord(value.exampleSection)) return false;
  return typeof value.baseId === 'string'
    && typeof value.courseName === 'string'
    && typeof value.exampleSection.campus === 'string'
    && Array.isArray(value.campuses)
    && Array.isArray(value.instructors)
    && Array.isArray(value.meetingTypes);
}

function isLegacySectionRow(value: unknown): value is RutgersCourse {
  if (!isRecord(value)) return false;
  return typeof value.baseId === 'string'
    && typeof value.courseName === 'string'
    && typeof value.campus === 'string'
    && Array.isArray(value.meetingTimes)
    && Array.isArray(value.instructors);
}

/**
 * A CDN can briefly serve the pre-compact `/api/courses` response after a
 * deployment. Normalize that complete legacy section payload instead of
 * trusting a TypeScript cast and crashing Explore at `exampleSection.campus`.
 */
function normalizeExploreCatalog(payload: unknown): ExploreCatalogSummary[] {
  if (!Array.isArray(payload) || !payload.length) {
    throw new Error('No catalog data is available for this semester.');
  }
  if (payload.every(isExploreSummary)) return payload;
  if (payload.every(isLegacySectionRow)) return createExploreCatalogSummaries(payload);
  throw new Error('Explore catalog data is incompatible. Refresh the page and try again.');
}

/** Client-safe catalog boundary: course JSON stays behind the semester API. */
export async function fetchCatalogCourses(semester: string, signal?: AbortSignal): Promise<RutgersCourse[]> {
  const response = await fetch(`/api/courses?semester=${encodeURIComponent(semester)}`, { signal });
  if (!response.ok) throw new Error(`Course catalog request failed (${response.status}).`);

  const courses = await response.json() as unknown;
  if (!Array.isArray(courses) || !courses.every((row) => isLegacySectionRow(row)
    && typeof row.id === 'string' && typeof row.department === 'string' && typeof row.class_number === 'string')) {
    throw new Error('Course catalog data is incompatible. Refresh the page and try again.');
  }
  return courses as RutgersCourse[];
}

/** Compact Explore catalog: one course summary instead of every section row. */
export async function fetchExploreCatalog(semester: string, signal?: AbortSignal): Promise<ExploreCatalogSummary[]> {
  const response = await fetch(`/api/courses?semester=${encodeURIComponent(semester)}&view=explore-list-v1`, { signal });
  if (!response.ok) throw new Error(`Explore catalog request failed (${response.status}).`);

  return normalizeExploreCatalog(await response.json() as unknown);
}

/** One selected course's complete inspector facts, including verified Wiki identities. */
export async function fetchExploreCourseDetails(semester: string, baseId: string, signal?: AbortSignal): Promise<{ summary: ExploreCatalogSummary; sections: RutgersCourse[] }> {
  const params = new URLSearchParams({ semester, course: baseId, view: 'explore-detail-v1' });
  const response = await fetch(`/api/courses?${params}`, { signal });
  if (!response.ok) throw new Error(`Course detail request failed (${response.status}).`);
  const payload: unknown = await response.json();
  if (!isRecord(payload) || !isExploreSummary(payload.summary) || !Array.isArray(payload.sections)
    || !payload.sections.length || !payload.sections.every(isLegacySectionRow)) {
    throw new Error('Course detail is incompatible. Refresh the page and try again.');
  }
  const summary = payload.summary;
  const requestedId = baseId.replace(/\D/g, '');
  if (summary.baseId.replace(/\D/g, '') !== requestedId && !payload.sections.some((section) => section.id === requestedId)) {
    throw new Error('Course detail is incompatible. Refresh the page and try again.');
  }
  if (payload.sections.some((section) => section.baseId !== summary.baseId)) {
    throw new Error('Course detail is incompatible. Refresh the page and try again.');
  }
  return { summary, sections: payload.sections };
}

/** Full section records are loaded only when Explore opens a course detail dialog. */
export async function fetchCatalogCourseDetails(semester: string, baseId: string, signal?: AbortSignal): Promise<RutgersCourse[]> {
  const params = new URLSearchParams({ semester, course: baseId });
  const response = await fetch(`/api/courses?${params}`, { signal });
  if (!response.ok) throw new Error(`Course detail request failed (${response.status}).`);

  const courses = await response.json() as unknown;
  if (!Array.isArray(courses) || !courses.length) throw new Error('Course detail is unavailable.');
  return courses as RutgersCourse[];
}

// Keep only parsed API responses in browser memory. This deliberately caches by
// semester rather than importing any of the checked-in term JSON modules.
const catalogRequests = new Map<string, Promise<RutgersCourse[]>>();

export function loadCatalogCourses(semester: string = DEFAULT_SEMESTER_ID): Promise<RutgersCourse[]> {
  const existing = catalogRequests.get(semester);
  if (existing) return existing;

  const request = fetchCatalogCourses(semester).catch((error) => {
    // A rejected request must not poison later retries for this semester.
    catalogRequests.delete(semester);
    throw error;
  });
  catalogRequests.set(semester, request);
  return request;
}

export function clearCatalogCourseCache(): void {
  catalogRequests.clear();
}

/** Resolve only the saved registration indexes; never download a whole term for a preview. */
export async function fetchScheduleCourses(semester: string, references: string[], signal?: AbortSignal): Promise<RutgersCourse[]> {
  const indexes = [...new Set(references.map((reference) => reference.split(':').at(-1) ?? '').filter((id) => /^\d{5}$/.test(id)))];
  if (indexes.length === 0) return [];
  if (indexes.length > 24) throw new Error('Schedule exceeds preview limit');
  const response = await fetch(`/api/courses?${new URLSearchParams({ semester, sections: indexes.join(',') })}`, { signal });
  if (!response.ok) throw new Error('Could not load schedule courses');
  return response.json();
}
